/* global faceapi */
import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, CheckCircle2, Loader2, RefreshCw, AlertTriangle, Power } from "lucide-react";
import { ensureModels, warmUpFaceModels, resetFaceScanner } from "@/lib/faceModels";

const POSES = ["Look straight at the camera", "Now turn your head slightly left", "Now turn slightly right"];
const HOLD_STILL = "Hold still…";
const dist = (a, b) => Math.sqrt(a.reduce((s, v, i) => s + (v - b[i]) ** 2, 0));

function judgeFace(det, v) {
    if (!det) return "Look at the camera";
    const { box, score } = det;
    const ratio = box.width / v.videoWidth;
    const cx = (box.x + box.width / 2) / v.videoWidth;
    const cy = (box.y + box.height / 2) / v.videoHeight;
    if (ratio < 0.2) return "Move a little closer";
    if (ratio > 0.75) return "Move back slightly";
    if (Math.abs(cx - 0.5) > 0.18 || Math.abs(cy - 0.5) > 0.22) return "Center your face in the oval";
    if (score < 0.7) return "Find better lighting";
    return HOLD_STILL;
}

// Watches the camera and takes an enrollment shot after 3 steady, well-framed frames.
function useAutoCapture({ enabled, videoRef, workingRef, lastCapRef, samplesRef, addSample, setHint }) {
    useEffect(() => {
        if (!enabled) { setHint(""); return undefined; }
        let stop = false;
        let good = 0;
        let timer;
        const tick = async () => {
            const v = videoRef.current;
            if (!stop && v && v.readyState >= 2 && !workingRef.current && Date.now() - lastCapRef.current > 1500) {
                workingRef.current = true;
                try {
                    const fa = window.faceapi;
                    const det = await fa.detectSingleFace(v, new fa.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }));
                    if (stop) return;
                    const h = judgeFace(det, v);
                    setHint(h);
                    good = h === HOLD_STILL ? good + 1 : 0;
                    if (good >= 3) {
                        good = 0;
                        const res = await fa.detectSingleFace(v, new fa.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 })).withFaceLandmarks().withFaceDescriptor();
                        const prev = samplesRef.current[samplesRef.current.length - 1];
                        if (!stop && res?.descriptor) {
                            const d = Array.from(res.descriptor);
                            if (!prev || dist(prev, d) >= 0.06 || Date.now() - lastCapRef.current > 5000) addSample(d);
                        }
                    }
                } catch (_) { /* keep watching */ } finally {
                    workingRef.current = false;
                }
            }
            if (!stop) timer = setTimeout(tick, 250);
        };
        tick();
        return () => { stop = true; clearTimeout(timer); };
    }, [enabled, videoRef, workingRef, lastCapRef, samplesRef, addSample, setHint]);
}

function explainCameraError(e) {
    const name = e?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") {
        return "Camera permission was denied. Tap the lock/camera icon in your browser's address bar and allow camera access for this site, then reload.";
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") {
        return "No front-facing camera was found on this device.";
    }
    if (name === "NotReadableError") {
        return "Your camera is already in use by another app. Close other apps using the camera (Zoom, FaceTime, etc.) and try again.";
    }
    if (name === "AbortError") {
        return "Camera was interrupted. Tap Enable camera to try again.";
    }
    return e?.message || "Could not open the camera.";
}

/**
 * FaceCapture
 *
 * Two modes:
 *  - mode="enroll": collects `requiredSamples` (default 3) face descriptors before completing.
 *  - mode="verify": captures a single descriptor on demand.
 *
 * Calls onComplete(descriptors[]) when finished (enroll) or onCapture(descriptor) (verify).
 *
 * IMPORTANT: Mobile browsers (iOS Safari, Chrome Android) require getUserMedia to be
 * invoked inside a user-gesture handler — otherwise no permission prompt appears.
 * We therefore start in the "idle" phase with an explicit "Enable camera" tap target.
 */
export default function FaceCapture({ mode = "verify", requiredSamples = 3, onComplete, onCapture, ctaLabel }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const cancelledRef = useRef(false);
    // idle (no permission requested yet) | loading | ready | capturing | error
    const [phase, setPhase] = useState("idle");
    const [error, setError] = useState("");
    const [samples, setSamples] = useState([]);   // [descriptor[]]
    const [busy, setBusy] = useState(false);
    const [modelsReady, setModelsReady] = useState(false);
    const [hint, setHint] = useState("");
    const [flash, setFlash] = useState(false);
    const samplesRef = useRef([]);
    const workingRef = useRef(false);
    const lastCapRef = useRef(0);

    // Start downloading the face scanner as soon as the component shows, before the tap.
    useEffect(() => {
        ensureModels().then(() => { if (!cancelledRef.current) setModelsReady(true); warmUpFaceModels(); }).catch(() => {});
    }, []);

    // Always cleanup the stream on unmount
    useEffect(() => {
        cancelledRef.current = false;
        return () => {
            cancelledRef.current = true;
            if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        };
    }, []);

    // Tap-to-enable: runs inside a user gesture so iOS Safari + Android Chrome will
    // actually show the permission prompt.
    const enableCamera = useCallback(async () => {
        if (phase === "loading" || phase === "ready" || phase === "capturing") return;
        setError("");
        setPhase("loading");
        try {
            const modelsPromise = ensureModels();

            // The constraints below are intentionally permissive — strict width/height
            // can cause OverconstrainedError on phones with non-standard sensors.
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "user" },
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                },
                audio: false,
            });
            if (cancelledRef.current) {
                stream.getTracks().forEach((t) => t.stop());
                return;
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // iOS requires playsInline + muted + a user-gesture .play() — we're inside
                // the click handler so this is allowed.
                try { await videoRef.current.play(); } catch (_) { /* ignore */ }
            }
            // Show the live camera right away; the scanner keeps loading in the background.
            setPhase("ready");
            modelsPromise.then(() => !cancelledRef.current && setModelsReady(true)).catch((e) => {
                if (cancelledRef.current) return;
                streamRef.current?.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
                setError(`${e.message}. Check your internet connection and tap Try again.`);
                setPhase("error");
            });
        } catch (e) {
            console.warn("FaceCapture: enableCamera failed", e);
            setError(explainCameraError(e));
            setPhase("error");
            // Drop any partial stream
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
        }
    }, [phase]);

    const captureOne = useCallback(async () => {
        if (!videoRef.current || workingRef.current) return null;
        workingRef.current = true;
        setBusy(true);
        try {
            const opts = new window.faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.55 });
            const res = await window.faceapi
                .detectSingleFace(videoRef.current, opts)
                .withFaceLandmarks()
                .withFaceDescriptor();
            if (!res || !res.descriptor) {
                setError("No face detected. Center your face and try again.");
                return null;
            }
            setError("");
            return Array.from(res.descriptor);
        } finally {
            workingRef.current = false;
            setBusy(false);
        }
    }, []);

    const addSample = useCallback((d) => {
        const next = [...samplesRef.current, d];
        samplesRef.current = next;
        lastCapRef.current = Date.now();
        setSamples(next);
        setFlash(true);
        setTimeout(() => setFlash(false), 350);
        if (navigator.vibrate) navigator.vibrate(60);
        if (next.length >= requiredSamples) onComplete?.(next);
    }, [requiredSamples, onComplete]);

    const onCaptureClick = useCallback(async () => {
        const d = await captureOne();
        if (!d) return;
        if (mode === "verify") {
            onCapture?.(d);
            return;
        }
        addSample(d);
    }, [captureOne, mode, addSample, onCapture]);

    useAutoCapture({ enabled: mode === "enroll" && phase === "ready" && modelsReady && samples.length < requiredSamples,
        videoRef, workingRef, lastCapRef, samplesRef, addSample, setHint });

    const reset = () => { samplesRef.current = []; setSamples([]); };
    const pose = POSES[Math.min(samples.length, POSES.length - 1)];
    const steady = hint === HOLD_STILL;

    return (
        <div className="border border-ink/10 bg-white p-5" data-testid="face-capture">
            <div className="overline mb-2 flex items-center gap-2">
                <Camera className="w-3.5 h-3.5" /> {mode === "enroll" ? "Face enrollment" : "Face verification"}
            </div>
            <div className="relative bg-ink/5 aspect-[4/3] w-full overflow-hidden">
                {/* Always render the video so its srcObject ref stays stable across phases */}
                <video
                    ref={videoRef}
                    muted
                    playsInline
                    autoPlay
                    className={`w-full h-full object-cover ${phase === "ready" || phase === "capturing" ? "" : "opacity-0"}`}
                    data-testid="face-capture-video"
                />
                {phase === "idle" && (
                    <button
                        type="button"
                        onClick={enableCamera}
                        data-testid="face-capture-enable-btn"
                        className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-ink/70 hover:bg-ink/10 transition-colors"
                    >
                        <div className="w-14 h-14 bg-klein text-white flex items-center justify-center rounded-full shadow-md">
                            <Power className="w-6 h-6" />
                        </div>
                        <div className="text-sm font-medium text-ink">Tap to enable camera</div>
                        <div className="text-xs text-ink/50 px-6 text-center">
                            Your browser will ask for camera permission. Nothing is uploaded — face matching happens on this device.
                        </div>
                    </button>
                )}
                {phase === "loading" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/10">
                        <Loader2 className="w-6 h-6 animate-spin text-ink/70" />
                        <div className="text-xs text-ink/60">Opening camera…</div>
                    </div>
                )}
                {phase === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-5 bg-red-50/95 text-red-800" data-testid="face-capture-error">
                        <AlertTriangle className="w-7 h-7" />
                        <div className="text-sm font-medium">Camera unavailable</div>
                        <div className="text-xs text-center max-w-sm">{error}</div>
                        <button
                            type="button"
                            onClick={enableCamera}
                            data-testid="face-capture-retry-btn"
                            className="mt-2 inline-flex items-center gap-2 bg-red-700 text-white px-4 py-2 text-xs hover:bg-red-800"
                        >
                            <RefreshCw className="w-3 h-3" /> Try again
                        </button>
                        <button
                            type="button"
                            onClick={resetFaceScanner}
                            data-testid="face-capture-reset-btn"
                            className="text-xs underline underline-offset-2 text-red-700 hover:text-red-900"
                        >
                            Still failing? Clear &amp; reload scanner
                        </button>
                    </div>
                )}
                {phase === "ready" && !modelsReady && (
                    <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 bg-ink/70 text-white text-xs px-2.5 py-1 rounded-full" data-testid="face-capture-preparing">
                        <Loader2 className="w-3 h-3 animate-spin" /> Preparing face scanner…
                    </div>
                )}
                {mode === "enroll" && phase === "ready" && modelsReady && samples.length < requiredSamples && (
                    <>
                        <div className={`pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[78%] aspect-[3/4] rounded-[50%] border-[3px] transition-colors ${steady ? "border-emerald-400" : "border-white/80"}`}
                            style={{ boxShadow: "0 0 0 9999px rgba(11,18,38,0.35)" }} data-testid="face-capture-oval" />
                        <div className="absolute bottom-3 inset-x-3 text-center" data-testid="face-capture-hint">
                            <div className="inline-block bg-ink/75 text-white rounded-xl px-3 py-1.5">
                                <div className="text-sm font-semibold">{pose}</div>
                                {hint && <div className={`text-xs ${steady ? "text-emerald-300" : "text-white/75"}`}>{hint}</div>}
                            </div>
                        </div>
                    </>
                )}
                {flash && <div className="pointer-events-none absolute inset-0 bg-white/70 animate-fade-in" data-testid="face-capture-flash" />}
                {mode === "enroll" && samples.length > 0 && (phase === "ready" || phase === "capturing") && (
                    <div className="absolute top-2 right-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-sm" data-testid="face-capture-sample-count">
                        {samples.length} / {requiredSamples} captured
                    </div>
                )}
            </div>
            {error && phase !== "error" && (
                <div className="mt-3 text-xs text-red-700" data-testid="face-capture-message">{error}</div>
            )}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={onCaptureClick}
                    disabled={phase !== "ready" || busy || !modelsReady}
                    data-testid="face-capture-shutter"
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                    {ctaLabel || (mode === "enroll" ? "Capture face" : "Verify face")}
                </button>
                {mode === "enroll" && samples.length > 0 && (
                    <button
                        type="button"
                        onClick={reset}
                        data-testid="face-capture-reset"
                        className="inline-flex items-center gap-2 text-ink/60 px-3 py-2 text-xs hover:text-ink"
                    >
                        <RefreshCw className="w-3 h-3" /> Start over
                    </button>
                )}
                {mode === "enroll" && samples.length >= requiredSamples && (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Enrollment ready
                    </span>
                )}
            </div>
            {mode === "enroll" && phase !== "idle" && (
                <p className="mt-3 text-xs text-ink/50 leading-relaxed">
                    Hold still inside the oval — <strong>{requiredSamples}</strong> shots are taken automatically (straight, slight left, slight right). If it struggles, tap Capture face. Good lighting helps.
                </p>
            )}
        </div>
    );
}
